import React from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import TableFilterTabs from './TableFilterTabs';
import TableToolbar from './TableToolbar';
import TablePagination from './TablePagination';

/**
 * List table shell — controls and pagination live outside the table surface.
 *
 * variant="flat" (default): full-width elevated table surface (border + shadow).
 * variant="contained": same surface styling, intended for dashboard panels.
 *
 * Pass `tabs` only when a page needs filter tabs — omitted by default.
 */
export default function TableLayout({
  variant = 'flat',
  tabs,
  toolbar,
  pagination,
  paginationContainer,
  children,
  sx = {},
}) {
  const { semantic, radiusPx, shadows } = useFeatureTheme();
  const tableTokens = semantic.table ?? {};

  const hasControls = Boolean(tabs?.items?.length || toolbar);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...sx,
      }}
    >
      {hasControls ? (
        <Box
          data-testid="table-layout-controls"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: tabs?.items?.length ? 1.5 : 0,
            width: '100%',
          }}
        >
          {tabs?.items?.length ? (
            <TableFilterTabs
              value={tabs.value}
              onChange={tabs.onChange}
              items={tabs.items}
              ariaLabel={tabs.ariaLabel}
            />
          ) : null}

          {toolbar ? (
            <TableToolbar search={toolbar.search} actions={toolbar.actions} />
          ) : null}
        </Box>
      ) : null}

      <Box
        data-testid="table-layout-surface"
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: semantic.surfaces.surface,
          border: `1px solid ${semantic.borders.border}`,
          borderRadius: radiusPx.lg,
          boxShadow: tableTokens.surfaceShadow ?? shadows.sm,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ width: '100%', overflow: 'visible' }}>{children}</Box>
      </Box>

      {pagination && paginationContainer === undefined ? (
        <Box data-testid="table-layout-footer" sx={{ width: '100%' }}>
          <TablePagination {...pagination} />
        </Box>
      ) : null}
      {pagination && paginationContainer
        ? createPortal(
          <Box data-testid="table-layout-footer" sx={{ width: '100%' }}>
            <TablePagination {...pagination} />
          </Box>,
          paginationContainer
        )
        : null}
    </Box>
  );
}
